import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowRight,
  IconCalculator,
  IconFileText,
  IconPlus,
  IconRefresh,
  IconSchool,
  IconTrash,
} from "@tabler/icons-react";
import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
} from "../../seo/utils";
import "./GpaCalculatorPage.css";

type NumberValue = number | "";
type GpaMode = "semester" | "cumulative" | "highSchool" | "weighted";
type CourseLevel = "regular" | "honors" | "apIb" | "custom";

export const gradePoints4Scale = {
  "A+": 4.0,
  A: 4.0,
  "A-": 3.7,
  "B+": 3.3,
  B: 3.0,
  "B-": 2.7,
  "C+": 2.3,
  C: 2.0,
  "C-": 1.7,
  "D+": 1.3,
  D: 1.0,
  "D-": 0.7,
  F: 0,
};

type GradeLetter = keyof typeof gradePoints4Scale;

type CourseRow = {
  id: string;
  name: string;
  credits: NumberValue;
  grade: GradeLetter;
};

type HighSchoolClassRow = CourseRow & {
  courseType: Exclude<CourseLevel, "custom">;
};

type WeightedCourseRow = CourseRow & {
  courseLevel: CourseLevel;
  customBoost: NumberValue;
};

const TOOL_PATH = "/tools/gpa-calculator";
const TOOLS_PATH = "/tools";

const courseTypeBoost = {
  regular: 0,
  honors: 0.5,
  apIb: 1.0,
};

const gradeOptions = Object.keys(gradePoints4Scale).map((grade) => ({
  value: grade,
  label: grade,
}));

const courseLevelOptions = [
  { value: "regular", label: "Regular" },
  { value: "honors", label: "Honors" },
  { value: "apIb", label: "AP / IB" },
];

const weightedCourseLevelOptions = [
  ...courseLevelOptions,
  { value: "custom", label: "Custom" },
];

const defaultCourses: CourseRow[] = [
  { id: "math", name: "Math", credits: 3, grade: "A" },
  { id: "english", name: "English", credits: 3, grade: "B+" },
  { id: "biology", name: "Biology", credits: 4, grade: "A-" },
];

const defaultHighSchoolClasses: HighSchoolClassRow[] = [
  { id: "algebra", name: "Algebra", credits: 1, grade: "A", courseType: "regular" },
  { id: "english-honors", name: "English Honors", credits: 1, grade: "B+", courseType: "honors" },
  { id: "ap-biology", name: "AP Biology", credits: 1, grade: "A-", courseType: "apIb" },
];

const defaultWeightedCourses: WeightedCourseRow[] = [
  { id: "history", name: "History", credits: 3, grade: "A", courseLevel: "regular", customBoost: 0 },
  { id: "chemistry-honors", name: "Chemistry Honors", credits: 4, grade: "B+", courseLevel: "honors", customBoost: 0 },
  { id: "ap-lit", name: "AP Literature", credits: 3, grade: "A-", courseLevel: "apIb", customBoost: 0 },
];

const faqs = [
  {
    question: "How do I calculate GPA?",
    answer:
      "To calculate GPA, multiply each course grade point by its credits, add the total grade points, and divide by the total credits.",
  },
  {
    question: "How do I calculate cumulative GPA?",
    answer:
      "To calculate cumulative GPA, combine your current GPA and completed credits with your new semester GPA and new credits.",
  },
  {
    question: "What is a college GPA calculator?",
    answer:
      "A college GPA calculator estimates your GPA using course credits and letter grades, usually on a 4.0 scale.",
  },
  {
    question: "What is a high school GPA calculator?",
    answer:
      "A high school GPA calculator estimates GPA from class grades and may include weighted points for honors, AP, or IB courses.",
  },
  {
    question: "What is the difference between weighted and unweighted GPA?",
    answer:
      "Unweighted GPA uses a standard 4.0 scale. Weighted GPA adds extra points for advanced classes such as honors, AP, or IB courses.",
  },
  {
    question: "Is an A+ a 4.0?",
    answer:
      "In many 4.0 GPA scales, both A+ and A are counted as 4.0. Some schools may use different scales, so check your official grading policy.",
  },
  {
    question: "Can this calculator be used for high school and college?",
    answer:
      "Yes. This GPA calculator includes modes for semester GPA, cumulative GPA, college GPA, high school GPA, and weighted GPA.",
  },
];

const gpaScaleRows = [
  ["A+ / A", "4.0"],
  ["A-", "3.7"],
  ["B+", "3.3"],
  ["B", "3.0"],
  ["B-", "2.7"],
  ["C+", "2.3"],
  ["C", "2.0"],
  ["C-", "1.7"],
  ["D+", "1.3"],
  ["D", "1.0"],
  ["D-", "0.7"],
  ["F", "0.0"],
];

export function calculateSemesterGPA(courses: CourseRow[]) {
  const validCourses = courses.filter(
    (course) =>
      Number(course.credits) > 0 && gradePoints4Scale[course.grade] !== undefined,
  );

  const totalCredits = validCourses.reduce(
    (sum, course) => sum + Number(course.credits),
    0,
  );

  const totalGradePoints = validCourses.reduce((sum, course) => {
    return sum + Number(course.credits) * gradePoints4Scale[course.grade];
  }, 0);

  if (totalCredits <= 0) return null;

  return {
    gpa: totalGradePoints / totalCredits,
    totalCredits,
  };
}

export function calculateCumulativeGPA(
  currentGPA: NumberValue,
  completedCredits: NumberValue,
  newSemesterGPA: NumberValue,
  newSemesterCredits: NumberValue,
) {
  const current = Number(currentGPA);
  const completed = Number(completedCredits);
  const semester = Number(newSemesterGPA);
  const semesterCredits = Number(newSemesterCredits);

  if (
    Number.isNaN(current) ||
    Number.isNaN(completed) ||
    Number.isNaN(semester) ||
    Number.isNaN(semesterCredits)
  ) {
    return null;
  }

  if (current < 0 || semester < 0 || completed < 0 || semesterCredits <= 0) {
    return null;
  }

  const totalCredits = completed + semesterCredits;

  if (totalCredits <= 0) {
    return null;
  }

  return {
    gpa:
      (current * completed + semester * semesterCredits) /
      totalCredits,
    totalCredits,
  };
}

export function calculateHighSchoolGPA(classes: HighSchoolClassRow[]) {
  const validClasses = classes.filter(
    (item) =>
      Number(item.credits) > 0 && gradePoints4Scale[item.grade] !== undefined,
  );

  const totalCredits = validClasses.reduce(
    (sum, item) => sum + Number(item.credits),
    0,
  );

  if (totalCredits <= 0) return null;

  const unweightedPoints = validClasses.reduce((sum, item) => {
    return sum + gradePoints4Scale[item.grade] * Number(item.credits);
  }, 0);

  const weightedPoints = validClasses.reduce((sum, item) => {
    const boost = courseTypeBoost[item.courseType] || 0;
    const weightedGradePoint = Math.min(
      gradePoints4Scale[item.grade] + boost,
      5.0,
    );
    return sum + weightedGradePoint * Number(item.credits);
  }, 0);

  return {
    unweightedGPA: unweightedPoints / totalCredits,
    weightedGPA: weightedPoints / totalCredits,
    totalCredits,
  };
}

export function roundGPA(value: number) {
  return Math.round(value * 100) / 100;
}

function calculateWeightedGPA(courses: WeightedCourseRow[]) {
  const validCourses = courses.filter(
    (course) =>
      Number(course.credits) > 0 && gradePoints4Scale[course.grade] !== undefined,
  );
  const totalCredits = validCourses.reduce(
    (sum, course) => sum + Number(course.credits),
    0,
  );

  if (totalCredits <= 0) return null;

  const weightedPoints = validCourses.reduce((sum, course) => {
    const boost =
      course.courseLevel === "custom"
        ? Number(course.customBoost || 0)
        : courseTypeBoost[course.courseLevel] || 0;
    const weightedGradePoint = Math.min(
      gradePoints4Scale[course.grade] + Math.max(0, boost),
      5.0,
    );
    return sum + weightedGradePoint * Number(course.credits);
  }, 0);

  return {
    gpa: weightedPoints / totalCredits,
    totalCredits,
  };
}

function toNumberInputValue(value: string | number): NumberValue {
  if (value === "") {
    return "";
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : "";
}

function numberValue(value: NumberValue) {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function formatGPA(value: number) {
  return roundGPA(value).toFixed(2);
}

function formatNumber(value: number) {
  const rounded = roundGPA(value);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function createId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.round(Math.random() * 1000)}`;
}

function GpaCalculatorPage() {
  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "GPA Calculator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "All",
    url: buildCanonicalUrl(TOOL_PATH),
    description:
      "Free GPA calculator to calculate semester GPA, cumulative GPA, college GPA, high school GPA, and weighted GPA.",
    isAccessibleForFree: true,
  };

  return (
    <Container fluid className="gpa-page" px={0}>
      <Seo
        title="GPA Calculator | College, High School & Cumulative GPA"
        description="Use our free GPA Calculator to calculate semester GPA, cumulative GPA, college GPA, and high school GPA. Add courses, credits, and grades instantly."
        path={TOOL_PATH}
        ogTitle="GPA Calculator"
        ogDescription="Calculate semester GPA, cumulative GPA, college GPA, high school GPA, and weighted GPA."
        keywords={[
          "gpa calculator",
          "college gpa calculator",
          "high school gpa calculator",
          "cumulative gpa calculator",
          "how to calculate gpa",
          "calculate gpa",
          "gpa calc",
        ]}
        schema={[
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: TOOLS_PATH },
            { name: "GPA Calculator", path: TOOL_PATH },
          ]),
          createFaqSchema(faqs),
          webApplicationSchema,
        ]}
      />

      <Box className="gpa-hero">
        <Container size="xl">
          <Stack gap="lg">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to={TOOLS_PATH}>
                Tools
              </Anchor>
              <Text>GPA Calculator</Text>
            </Breadcrumbs>

            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing={34}>
              <Stack gap="md" justify="center">
                <Group gap="sm">
                  <Badge color="green" radius="sm" size="lg">
                    Grade & GPA Tools
                  </Badge>
                  <Badge color="blue" radius="sm" size="lg" variant="light">
                    College and high school GPA
                  </Badge>
                </Group>
                <ThemeIcon size={58} radius="md" color="green" variant="light">
                  <IconCalculator size={32} />
                </ThemeIcon>
                <Title order={1} className="gpa-title">
                  GPA Calculator
                </Title>
                <Text size="lg" c="dimmed" className="gpa-intro">
                  Use our free GPA Calculator to calculate your semester GPA,
                  cumulative GPA, college GPA, or high school GPA. Add your
                  courses, credits, and grades to estimate your GPA instantly.
                </Text>
              </Stack>

              <Paper
                withBorder
                radius="md"
                p={{ base: "md", md: "xl" }}
                className="gpa-panel"
              >
                <GpaCalculator />
              </Paper>
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <main className="gpa-content">
          <GpaSeoContent />
          <GpaCta />
          <RelatedGpaTools />
          <GpaFAQ />
        </main>
      </Container>
    </Container>
  );
}

export function GpaCalculator() {
  const [mode, setMode] = useState<GpaMode>("semester");

  return (
    <Stack gap="lg" component="section" aria-label="GPA calculator">
      <Stack gap={6}>
        <Title order={2}>Calculate GPA</Title>
        <Text c="dimmed">
          Choose semester, cumulative, high school, or weighted GPA mode.
        </Text>
      </Stack>

      <SegmentedControl
        fullWidth
        value={mode}
        onChange={(value) => setMode(value as GpaMode)}
        className="gpa-mode-control"
        data={[
          { value: "semester", label: "Semester GPA Calculator" },
          { value: "cumulative", label: "Cumulative GPA Calculator" },
          { value: "highSchool", label: "High School GPA Calculator" },
          { value: "weighted", label: "Weighted GPA Calculator" },
        ]}
        aria-label="GPA calculator mode"
      />

      {mode === "semester" ? <SemesterGpaCalculator /> : null}
      {mode === "cumulative" ? <CumulativeGpaCalculator /> : null}
      {mode === "highSchool" ? <HighSchoolGpaCalculator /> : null}
      {mode === "weighted" ? <WeightedGpaCalculator /> : null}

      <Alert color="blue" radius="md" variant="light" icon={<IconAlertCircle size={18} />}>
        GPA scales vary by school. Use this as a planning estimate and confirm
        your official grading policy.
      </Alert>
    </Stack>
  );
}

function SemesterGpaCalculator() {
  const [courses, setCourses] = useState<CourseRow[]>(defaultCourses);
  const errors = getCourseErrors(courses);
  const result = !errors.length ? calculateSemesterGPA(courses) : null;

  return (
    <Stack gap="lg">
      <CourseRows
        rows={courses}
        onRowsChange={setCourses}
      />
      <RowActions
        addLabel="Add Course"
        resetLabel="Reset Calculator"
        onAdd={() =>
          setCourses((current) => [
            ...current,
            { id: createId("course"), name: "", credits: "", grade: "A" },
          ])
        }
        onReset={() => setCourses(defaultCourses)}
      />
      <ValidationAlerts errors={errors} />
      {result ? (
        <GpaResult title="Your Semester GPA">
          <Text>
            Your semester GPA is <strong>{formatGPA(result.gpa)}</strong>.
          </Text>
          <Text>
            Total credits: <strong>{formatNumber(result.totalCredits)}</strong>
          </Text>
        </GpaResult>
      ) : null}
    </Stack>
  );
}

function CumulativeGpaCalculator() {
  const [currentGPA, setCurrentGPA] = useState<NumberValue>(3.2);
  const [completedCredits, setCompletedCredits] = useState<NumberValue>(60);
  const [newSemesterGPA, setNewSemesterGPA] = useState<NumberValue>(3.6);
  const [newSemesterCredits, setNewSemesterCredits] = useState<NumberValue>(15);
  const errors = [
    currentGPA === "" ? "Current GPA is required." : "",
    completedCredits === "" ? "Completed credits are required." : "",
    newSemesterGPA === "" ? "New semester GPA is required." : "",
    newSemesterCredits === "" ? "New semester credits are required." : "",
    numberValue(currentGPA) < 0 || numberValue(currentGPA) > 5
      ? "Current GPA should be between 0 and 5."
      : "",
    numberValue(completedCredits) < 0 ? "Completed credits cannot be negative." : "",
    numberValue(newSemesterGPA) < 0 || numberValue(newSemesterGPA) > 5
      ? "New semester GPA should be between 0 and 5."
      : "",
    numberValue(newSemesterCredits) <= 0
      ? "New semester credits must be greater than 0."
      : "",
  ].filter(Boolean);
  const result = !errors.length
    ? calculateCumulativeGPA(
        currentGPA,
        completedCredits,
        newSemesterGPA,
        newSemesterCredits,
      )
    : null;

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <NumberInput
          label="Current GPA"
          placeholder="e.g. 3.2"
          value={currentGPA}
          min={0}
          allowNegative={false}
          onChange={(value) => setCurrentGPA(toNumberInputValue(value))}
          aria-label="Current GPA"
        />
        <NumberInput
          label="Completed Credits"
          placeholder="e.g. 60"
          value={completedCredits}
          min={0}
          allowNegative={false}
          onChange={(value) => setCompletedCredits(toNumberInputValue(value))}
          aria-label="Completed credits"
        />
        <NumberInput
          label="New Semester GPA"
          placeholder="e.g. 3.6"
          value={newSemesterGPA}
          min={0}
          allowNegative={false}
          onChange={(value) => setNewSemesterGPA(toNumberInputValue(value))}
          aria-label="New semester GPA"
        />
        <NumberInput
          label="New Semester Credits"
          placeholder="e.g. 15"
          value={newSemesterCredits}
          min={0}
          allowNegative={false}
          onChange={(value) => setNewSemesterCredits(toNumberInputValue(value))}
          aria-label="New semester credits"
        />
      </SimpleGrid>
      <RowActions
        resetLabel="Reset Calculator"
        onReset={() => {
          setCurrentGPA(3.2);
          setCompletedCredits(60);
          setNewSemesterGPA(3.6);
          setNewSemesterCredits(15);
        }}
      />
      <ValidationAlerts errors={errors} />
      {result ? (
        <GpaResult title="Your Cumulative GPA">
          <Text>
            Your updated cumulative GPA is{" "}
            <strong>{formatGPA(result.gpa)}</strong>.
          </Text>
          <Text>
            Total credits after this semester:{" "}
            <strong>{formatNumber(result.totalCredits)}</strong>
          </Text>
        </GpaResult>
      ) : null}
    </Stack>
  );
}

function HighSchoolGpaCalculator() {
  const [classes, setClasses] = useState<HighSchoolClassRow[]>(
    defaultHighSchoolClasses,
  );
  const errors = getCourseErrors(classes);
  const result = !errors.length ? calculateHighSchoolGPA(classes) : null;

  function updateRow(id: string, updates: Partial<HighSchoolClassRow>) {
    setClasses((current) =>
      current.map((row) => (row.id === id ? { ...row, ...updates } : row)),
    );
  }

  return (
    <Stack gap="lg">
      <Stack gap="sm">
        {classes.map((row, index) => (
          <Paper key={row.id} withBorder radius="md" p="md" className="gpa-row">
            <SimpleGrid cols={{ base: 1, md: 5 }} spacing="sm">
              <TextInput
                label="Class Name"
                placeholder="e.g. Math, English, Biology"
                value={row.name}
                onChange={(event) => updateRow(row.id, { name: event.currentTarget.value })}
                aria-label={`Class name ${index + 1}`}
              />
              <GradeSelect
                value={row.grade}
                onChange={(grade) => updateRow(row.id, { grade })}
                label={`Grade ${index + 1}`}
              />
              <Select
                label="Course Type"
                data={courseLevelOptions}
                value={row.courseType}
                onChange={(value) =>
                  updateRow(row.id, {
                    courseType: (value as HighSchoolClassRow["courseType"]) || "regular",
                  })
                }
                allowDeselect={false}
                aria-label={`Course type ${index + 1}`}
              />
              <CreditsInput
                value={row.credits}
                onChange={(credits) => updateRow(row.id, { credits })}
                label={`Credits ${index + 1}`}
              />
              <RemoveRowButton
                disabled={classes.length === 1}
                onClick={() =>
                  setClasses((current) =>
                    current.filter((candidate) => candidate.id !== row.id),
                  )
                }
                label={`Remove class ${index + 1}`}
              />
            </SimpleGrid>
          </Paper>
        ))}
      </Stack>
      <RowActions
        addLabel="Add Class"
        resetLabel="Reset Calculator"
        onAdd={() =>
          setClasses((current) => [
            ...current,
            {
              id: createId("class"),
              name: "",
              credits: 1,
              grade: "A",
              courseType: "regular",
            },
          ])
        }
        onReset={() => setClasses(defaultHighSchoolClasses)}
      />
      <ValidationAlerts errors={errors} />
      {result ? (
        <GpaResult title="Your High School GPA">
          <Text>
            Unweighted GPA: <strong>{formatGPA(result.unweightedGPA)}</strong>
          </Text>
          <Text>
            Weighted GPA: <strong>{formatGPA(result.weightedGPA)}</strong>
          </Text>
          <Text>
            Total credits: <strong>{formatNumber(result.totalCredits)}</strong>
          </Text>
        </GpaResult>
      ) : null}
    </Stack>
  );
}

function WeightedGpaCalculator() {
  const [courses, setCourses] = useState<WeightedCourseRow[]>(
    defaultWeightedCourses,
  );
  const errors = getCourseErrors(courses).concat(
    courses
      .filter(
        (course) =>
          course.courseLevel === "custom" && numberValue(course.customBoost) < 0,
      )
      .map((course) => `${course.name || "Course"} custom GPA boost cannot be negative.`),
  );
  const result = !errors.length ? calculateWeightedGPA(courses) : null;

  function updateRow(id: string, updates: Partial<WeightedCourseRow>) {
    setCourses((current) =>
      current.map((row) => (row.id === id ? { ...row, ...updates } : row)),
    );
  }

  return (
    <Stack gap="lg">
      <Stack gap="sm">
        {courses.map((row, index) => (
          <Paper key={row.id} withBorder radius="md" p="md" className="gpa-row">
            <SimpleGrid
              cols={{ base: 1, md: row.courseLevel === "custom" ? 6 : 5 }}
              spacing="sm"
            >
              <TextInput
                label="Course Name"
                placeholder="e.g. Math, English, Biology"
                value={row.name}
                onChange={(event) => updateRow(row.id, { name: event.currentTarget.value })}
                aria-label={`Course name ${index + 1}`}
              />
              <CreditsInput
                value={row.credits}
                onChange={(credits) => updateRow(row.id, { credits })}
                label={`Credits ${index + 1}`}
              />
              <GradeSelect
                value={row.grade}
                onChange={(grade) => updateRow(row.id, { grade })}
                label={`Grade ${index + 1}`}
              />
              <Select
                label="Course Level"
                data={weightedCourseLevelOptions}
                value={row.courseLevel}
                onChange={(value) =>
                  updateRow(row.id, {
                    courseLevel: (value as CourseLevel) || "regular",
                  })
                }
                allowDeselect={false}
                aria-label={`Course level ${index + 1}`}
              />
              {row.courseLevel === "custom" ? (
                <NumberInput
                  label="Custom GPA Boost"
                  placeholder="e.g. 0.5"
                  value={row.customBoost}
                  min={0}
                  allowNegative={false}
                  onChange={(value) =>
                    updateRow(row.id, { customBoost: toNumberInputValue(value) })
                  }
                  aria-label={`Custom GPA boost ${index + 1}`}
                />
              ) : null}
              <RemoveRowButton
                disabled={courses.length === 1}
                onClick={() =>
                  setCourses((current) =>
                    current.filter((candidate) => candidate.id !== row.id),
                  )
                }
                label={`Remove course ${index + 1}`}
              />
            </SimpleGrid>
          </Paper>
        ))}
      </Stack>
      <RowActions
        addLabel="Add Course"
        resetLabel="Reset Calculator"
        onAdd={() =>
          setCourses((current) => [
            ...current,
            {
              id: createId("weighted"),
              name: "",
              credits: "",
              grade: "A",
              courseLevel: "regular",
              customBoost: 0,
            },
          ])
        }
        onReset={() => setCourses(defaultWeightedCourses)}
      />
      <ValidationAlerts errors={errors} />
      {result ? (
        <GpaResult title="Your Weighted GPA">
          <Text>
            Your weighted GPA is <strong>{formatGPA(result.gpa)}</strong>.
          </Text>
          <Text>
            Total credits: <strong>{formatNumber(result.totalCredits)}</strong>
          </Text>
        </GpaResult>
      ) : null}
    </Stack>
  );
}

function CourseRows({
  rows,
  onRowsChange,
}: {
  rows: CourseRow[];
  onRowsChange: (rows: CourseRow[]) => void;
}) {
  function updateRow(id: string, updates: Partial<CourseRow>) {
    onRowsChange(rows.map((row) => (row.id === id ? { ...row, ...updates } : row)));
  }

  return (
    <Stack gap="sm">
      {rows.map((row, index) => (
        <Paper key={row.id} withBorder radius="md" p="md" className="gpa-row">
          <SimpleGrid cols={{ base: 1, md: 4 }} spacing="sm">
            <TextInput
              label="Course Name"
              placeholder="e.g. Math, English, Biology"
              value={row.name}
              onChange={(event) => updateRow(row.id, { name: event.currentTarget.value })}
              aria-label={`Course name ${index + 1}`}
            />
            <CreditsInput
              value={row.credits}
              onChange={(credits) => updateRow(row.id, { credits })}
              label={`Credits ${index + 1}`}
            />
            <GradeSelect
              value={row.grade}
              onChange={(grade) => updateRow(row.id, { grade })}
              label={`Grade ${index + 1}`}
            />
            <RemoveRowButton
              disabled={rows.length === 1}
              onClick={() =>
                onRowsChange(rows.filter((candidate) => candidate.id !== row.id))
              }
              label={`Remove course ${index + 1}`}
            />
          </SimpleGrid>
        </Paper>
      ))}
    </Stack>
  );
}

function GradeSelect({
  value,
  onChange,
  label,
}: {
  value: GradeLetter;
  onChange: (grade: GradeLetter) => void;
  label: string;
}) {
  return (
    <Select
      label="Grade"
      data={gradeOptions}
      value={value}
      onChange={(nextValue) => onChange((nextValue as GradeLetter) || "A")}
      allowDeselect={false}
      aria-label={label}
    />
  );
}

function CreditsInput({
  value,
  onChange,
  label,
}: {
  value: NumberValue;
  onChange: (value: NumberValue) => void;
  label: string;
}) {
  return (
    <NumberInput
      label="Credits"
      placeholder="e.g. 3"
      value={value}
      min={0}
      allowNegative={false}
      onChange={(nextValue) => onChange(toNumberInputValue(nextValue))}
      aria-label={label}
    />
  );
}

function RemoveRowButton({
  disabled,
  onClick,
  label,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <Button
      mt={{ base: 0, md: 24 }}
      variant="light"
      color="red"
      leftSection={<IconTrash size={16} />}
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
    >
      Remove
    </Button>
  );
}

function RowActions({
  addLabel,
  resetLabel,
  onAdd,
  onReset,
}: {
  addLabel?: string;
  resetLabel: string;
  onAdd?: () => void;
  onReset: () => void;
}) {
  return (
    <Group gap="sm">
      {onAdd ? (
        <Button variant="light" leftSection={<IconPlus size={18} />} onClick={onAdd}>
          {addLabel}
        </Button>
      ) : null}
      <Button
        variant="outline"
        color="gray"
        leftSection={<IconRefresh size={18} />}
        onClick={onReset}
      >
        {resetLabel}
      </Button>
    </Group>
  );
}

function ValidationAlerts({ errors }: { errors: string[] }) {
  if (!errors.length) {
    return null;
  }

  return (
    <Alert icon={<IconAlertCircle size={18} />} color="red" radius="md">
      <ul className="gpa-alert-list">
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </Alert>
  );
}

function GpaResult({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box className="gpa-result" aria-live="polite">
      <Stack gap="sm">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </Box>
  );
}

function getCourseErrors(courses: CourseRow[]) {
  return courses.flatMap((course) => {
    const errors = [
      course.name.trim() ? "" : "Course name is required.",
      course.credits === "" || numberValue(course.credits) <= 0
        ? `${course.name || "Course"} credits must be greater than 0.`
        : "",
      gradePoints4Scale[course.grade] === undefined
        ? `${course.name || "Course"} grade is required.`
        : "",
    ];

    return errors.filter(Boolean);
  });
}

function GpaSeoContent() {
  return (
    <>
      <SeoTextSection title="How to Use the GPA Calculator">
        <Text>
          Select the GPA calculator mode you need, enter your courses, credits,
          and grades, and the calculator will estimate your GPA instantly. You
          can calculate semester GPA, cumulative GPA, high school GPA, or
          weighted GPA.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="How to Calculate GPA">
        <Text>
          To calculate GPA, multiply each course grade point by its credits, add
          all grade points together, and divide by the total number of credits.
        </Text>
        <FormulaBox>GPA = Total Grade Points / Total Credits</FormulaBox>
      </SeoTextSection>

      <SeoTextSection title="How to Calculate Cumulative GPA">
        <Text>
          Cumulative GPA combines your previous GPA and credits with your new
          semester GPA and credits. This helps you estimate your updated overall
          GPA after a new semester.
        </Text>
        <FormulaBox>
          Cumulative GPA = ((Current GPA x Completed Credits) + (New GPA x New
          Credits)) / Total Credits
        </FormulaBox>
      </SeoTextSection>

      <SeoTextSection title="College GPA Calculator">
        <Text>
          The college GPA calculator uses course credits and letter grades to
          estimate your GPA on a 4.0 scale. This is useful for semester planning,
          academic standing, scholarships, and graduation requirements.
        </Text>
      </SeoTextSection>

      <SeoTextSection title="High School GPA Calculator">
        <Text>
          The high school GPA calculator can estimate both unweighted and
          weighted GPA. Weighted GPA may include extra points for honors, AP, or
          IB classes depending on your school policy.
        </Text>
      </SeoTextSection>

      <section className="gpa-section">
        <Stack gap="md">
          <Title order={2}>GPA Calculator Examples</Title>
          <Text>
            If a 3-credit course has an A worth 4.0 points, it contributes 12
            grade points. If another 3-credit course has a B worth 3.0 points,
            it contributes 9 grade points. Together, 21 grade points divided by
            6 credits gives a 3.50 GPA.
          </Text>
        </Stack>
      </section>

      <GpaScaleTable />
    </>
  );
}

function SeoTextSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="gpa-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function FormulaBox({ children }: { children: ReactNode }) {
  return <Box className="gpa-formula formula-box">{children}</Box>;
}

function GpaScaleTable() {
  return (
    <section className="gpa-section">
      <Stack gap="md">
        <Title order={2}>GPA Scale Table</Title>
        <Box className="gpa-table-wrap">
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Letter Grade</Table.Th>
                <Table.Th>Grade Point</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {gpaScaleRows.map((row) => (
                <Table.Tr key={row.join("-")}>
                  <Table.Td>{row[0]}</Table.Td>
                  <Table.Td>{row[1]}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Stack>
    </section>
  );
}

function GpaCta() {
  return (
    <section className="gpa-cta cta-box">
      <Group justify="space-between" align="center" gap="lg">
        <Group gap="md" align="flex-start" wrap="nowrap">
          <ThemeIcon radius="md" size={54} color="blue">
            <IconSchool size={28} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={2}>Need Help Improving Your GPA?</Title>
            <Text c="dimmed" className="gpa-cta-copy">
              If your GPA is lower than expected, SubjectBuddy can connect you
              with academic support experts for assignments, coursework, exam
              preparation, and study guidance.
            </Text>
          </Stack>
        </Group>
        <Button
          component={Link}
          to="/assignment-help"
          radius="md"
          className="cta-button"
          rightSection={<IconArrowRight size={18} />}
        >
          Get Academic Help
        </Button>
      </Group>
    </section>
  );
}

function RelatedGpaTools() {
  const relatedItems = [
    {
      title: "Grade Calculator",
      href: "/tools/grade-calculator",
      description: "Calculate current, weighted, final, and test grades.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Final Grade Calculator",
      href: "/tools/final-grade-calculator",
      description: "Find what score you need on your final exam.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Weighted Grade Calculator",
      href: "/tools/weighted-grade-calculator",
      description: "Use weighted categories to estimate class grades.",
      icon: <IconCalculator size={20} />,
    },
    {
      title: "Test Grade Calculator",
      href: "/tools/test-grade-calculator",
      description: "Calculate test percentage from correct or wrong answers.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Assignment Help",
      href: "/assignment-help",
      description: "Get support on coursework that can affect your GPA.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Homework Help",
      href: "/homework-help",
      description: "Stay on track with homework before grades pile up.",
      icon: <IconFileText size={20} />,
    },
    {
      title: "Exam Help",
      href: "/exam-help",
      description: "Prepare for exams that can move your GPA.",
      icon: <IconSchool size={20} />,
    },
    {
      title: "Study Help",
      href: "/study-help",
      description: "Get study support when GPA goals feel hard to reach.",
      icon: <IconFileText size={20} />,
    },
  ];

  return (
    <section className="gpa-section">
      <Stack gap="md">
        <Title order={2}>Related Student Tools</Title>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {relatedItems.map((item) => (
            <Paper
              key={item.href}
              component={Link}
              to={item.href}
              withBorder
              radius="md"
              p="lg"
              className="gpa-link-card"
            >
              <Group gap="md" wrap="nowrap" align="flex-start">
                <ThemeIcon color="green" variant="light" radius="md">
                  {item.icon}
                </ThemeIcon>
                <Stack gap={4}>
                  <Text fw={800}>{item.title}</Text>
                  <Text size="sm" c="dimmed">
                    {item.description}
                  </Text>
                </Stack>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </section>
  );
}

function GpaFAQ() {
  return (
    <section className="gpa-section">
      <Stack gap="md">
        <Title order={2}>FAQs About GPA Calculator</Title>
        {faqs.map((faq) => (
          <Paper key={faq.question} withBorder radius="md" p="lg">
            <Stack gap={6}>
              <Title order={3}>{faq.question}</Title>
              <Text c="dimmed" style={{ lineHeight: 1.8 }}>
                {faq.answer}
              </Text>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </section>
  );
}

export default GpaCalculatorPage;
